import { useRef, useState, useEffect, type FunctionComponent, type ChangeEvent } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import noop from "lodash/noop";

import { DotsLoader } from "@similarweb/ui-components";

import { useKeywordGroupModalWorkflow } from "app/pages/keyword-analysis/useKeywordGroupModalWorkflow";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { useAllKeywordGroups } from "app/services/keyword-groups";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { keywordGroupsTransforms } from "app/services/keyword-groups/utils/keywordGroupsTransforms";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import { AutocompleteStyled } from "../AutocompleteKeywords/styles/AutocompleteKeywordStyles";
import { useAutoCompleteClick } from "../AutocompleteWebCategories/utils";

import { AutocompleteDemandAnalysisContent } from "./components/AutocompleteDemandAnalysisContent";
import { AutocompleteDemandAnalysisModals } from "./components/AutocompleteDemandAnalysisModals";
import { getAutocompleteData as getAutocompleteDataService } from "./services/AutocompleteDemandAnalysisDataService";
import type {
    AutocompleteDemandAnalysisItem,
    AutocompleteDemandAnalysisEntity,
} from "./types/AutocompleteDemandAnalysisTypes";
import * as navigationUtils from "./utils/autocompleteDemandAnalysisNavigation";
import { AUTOCOMPLETE_LIMIT_MODE } from "./utils/consts";

type IAutocompleteDemandAnalysisUnifiedProps = {
    autocompleteProps: Record<string, unknown>;
    handleSave: (modifiedGroup: IUserKeywordGroup) => void;
    selectedValue?: string;
    onClick?: (entity: AutocompleteDemandAnalysisEntity) => void;
    onKeyboardSelect?: (entity: AutocompleteDemandAnalysisEntity) => void;
    countryId?: number;
    onGroupShareModalOpened?: () => void;
    onGroupShareModalClosed?: () => void;
    onGroupEditorModalOpened?: () => void;
    onGroupEditorModalClosed?: () => void;
    onGroupDelete?: (deletedGroup: Pick<IUserKeywordGroup, "Id">) => void;
    keywordsGroupsToFilterFromAutocomplete?: string[];
    truncateResultsOnBodyClick?: boolean;
    showGoToMonitorKeywordListButton?: boolean;
    isPrimaryEntitySelection?: boolean;
    shouldConfirmDeleteKeywordList?: boolean;
};

export const AutocompleteDemandAnalysisUnified: FunctionComponent<IAutocompleteDemandAnalysisUnifiedProps> = ({
    selectedValue = "",
    countryId,
    autocompleteProps,
    onClick,
    onKeyboardSelect,
    onGroupShareModalOpened,
    onGroupShareModalClosed,
    onGroupEditorModalOpened,
    onGroupDelete,
    onGroupEditorModalClosed = noop,
    keywordsGroupsToFilterFromAutocomplete,
    truncateResultsOnBodyClick = true,
    showGoToMonitorKeywordListButton = false,
    handleSave,
    isPrimaryEntitySelection = false,
    shouldConfirmDeleteKeywordList = false,
}: IAutocompleteDemandAnalysisUnifiedProps) => {
    const t = useTranslation();
    const stateAndParams = useCurrentStateAndParams<{ country?: number }>();
    const countryIdFromParams = stateAndParams.params.country;
    const resolvedCountryId = countryId ?? countryIdFromParams ?? WORLDWIDE_COUNTRY_ID;

    const [isAutocompleteLoading, setIsAutocompleteLoading] = useState(false);
    const [isDirtyState, setIsDirtyState] = useState<boolean>(false);

    const [keywordQuery, setKeywordQuery] = useState<string>(selectedValue);

    useEffect(() => {
        setKeywordQuery(selectedValue);
    }, [selectedValue]);

    const resolveQueryInput = (query: string): string | null => {
        if (query) {
            return query;
        }

        return null;
    };

    const onAutocompleteBlur = () => {
        setIsDirtyState(false);
    };

    const {
        controller: editorController,
        handleBeforeDelete,
        deleteConfirmationModalProps,
    } = useKeywordGroupModalWorkflow({
        onDeleteSuccess: onGroupDelete ? (group) => onGroupDelete(group) : undefined,
        onEditorOpened: onGroupEditorModalOpened,
    });

    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [groupToShare, setGroupToShare] = useState<IUserKeywordGroup | null>(null);
    const { userGroups, sharedGroups } = useAllKeywordGroups();

    const handleGroupShareClick = (item: IUserKeywordGroup) => {
        const groupForModal = keywordGroupsTransforms.adaptGroupDataForShareModal(item, [
            ...userGroups,
            ...sharedGroups,
        ]) as IUserKeywordGroup;
        setGroupToShare(groupForModal);
        setIsShareModalOpen(true);
        onGroupShareModalOpened?.();
    };

    const handleGroupShareClose = () => {
        setIsShareModalOpen(false);
        setGroupToShare(null);
        onGroupShareModalClosed?.();
    };

    const handleGroupEditClick = (item: IUserKeywordGroup) => {
        editorController.openSession({ keywordsGroup: item });
        onGroupEditorModalOpened?.();
    };

    const closeEditorModal = () => {
        editorController.hide();
        onGroupEditorModalClosed();
    };

    const [isFocused, setIsFocused] = useState(false);

    const autocompleteRef = useRef(null);
    useAutoCompleteClick(autocompleteRef, truncateResultsOnBodyClick);

    const handleItemNavigation = (item: AutocompleteDemandAnalysisItem) => {
        navigationUtils.handleItemNavigation(item, resolvedCountryId);
    };

    const handleKeyboardSelect = (
        event: any, // eslint-disable-line @typescript-eslint/no-explicit-any
        selectedItemObj: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    ) => {
        navigationUtils.handleKeyboardSelect(
            event as ChangeEvent<HTMLInputElement>,
            selectedItemObj as AutocompleteDemandAnalysisItem | null,
            resolvedCountryId,
            onKeyboardSelect,
        );
    };

    const getAutocompleteData = async (queryInput: string): Promise<AutocompleteDemandAnalysisItem[]> => {
        setIsAutocompleteLoading(true);

        const query = resolveQueryInput(queryInput);
        let isNextStateDirty = isDirtyState;

        // If the query is the same as the initial query, we should not perform a new search
        // unless we are in a dirty state or the query is empty.
        if (query !== selectedValue || isDirtyState || !query?.length) {
            isNextStateDirty = true;
            setIsDirtyState(true);
            setKeywordQuery(query ?? "");
        }

        const searchText = (isNextStateDirty ? (query ?? "") : keywordQuery) || "";
        const hasQuery = searchText.length >= 2;

        // Determine limit mode based on query state and primary entity selection
        // - No query (showing recents): always use limited mode
        // - Has query: use limited mode only if it's a primary entity selection
        const limitMode =
            !hasQuery || isPrimaryEntitySelection ? AUTOCOMPLETE_LIMIT_MODE.limited : AUTOCOMPLETE_LIMIT_MODE.unlimited;

        try {
            const results = await getAutocompleteDataService(
                searchText,
                hasQuery,
                limitMode,
                keywordsGroupsToFilterFromAutocomplete,
            );
            setIsAutocompleteLoading(false);
            return results;
        } catch (_error) {
            setIsAutocompleteLoading(false);
            return [];
        }
    };

    const renderUnifiedAutocompleteContent = (autocompleteData: {
        listItems: AutocompleteDemandAnalysisItem[];
        selectedItemId: number;
    }) => {
        return (
            <AutocompleteDemandAnalysisContent
                showEditButton
                showShareButton
                data={autocompleteData.listItems}
                selectedIndex={autocompleteData.selectedItemId}
                onItemNavigation={handleItemNavigation}
                handleGroupEditClick={handleGroupEditClick}
                handleGroupShareClick={handleGroupShareClick}
                onClick={onClick}
                query={keywordQuery}
                sectionTitle={t("common.recent.searches")}
                isFocused={isFocused}
                isLoading={isAutocompleteLoading}
            />
        );
    };

    return (
        <>
            <AutocompleteDemandAnalysisModals
                isShareModalOpen={isShareModalOpen}
                groupToShare={groupToShare}
                handleGroupShareClose={handleGroupShareClose}
                controller={editorController}
                closeEditorModal={closeEditorModal}
                handleBeforeDelete={shouldConfirmDeleteKeywordList ? handleBeforeDelete : undefined}
                deleteConfirmationModalProps={shouldConfirmDeleteKeywordList ? deleteConfirmationModalProps : undefined}
                onGroupDelete={onGroupDelete}
                handleSave={handleSave}
                showGoToMonitorKeywordListButton={showGoToMonitorKeywordListButton}
            />

            <AutocompleteStyled
                floating
                preventTruncateUnlessForced
                shouldUpdateSelectedValue
                ref={autocompleteRef}
                isLoading={isAutocompleteLoading}
                className="AutocompleteWithTabs"
                getListItems={getAutocompleteData}
                getSelectableListItems={navigationUtils.getSelectableItems}
                renderItems={renderUnifiedAutocompleteContent}
                loadingComponent={<DotsLoader />}
                debounce={250}
                onBlur={() => {
                    onAutocompleteBlur();
                    setIsFocused(false);
                }}
                onFocus={() => {
                    setIsFocused(true);
                }}
                selectedValue={keywordQuery}
                overrideEnterFunc={(
                    event: any, // eslint-disable-line @typescript-eslint/no-explicit-any
                    selectedItemObj: any, // eslint-disable-line @typescript-eslint/no-explicit-any
                ) => handleKeyboardSelect(event, selectedItemObj)}
                {...autocompleteProps}
            />
        </>
    );
};
